import React, { useEffect, useState, useCallback } from "react";
import { Platform } from "../types";
import { RefreshCw, Trash2 } from "lucide-react";
import {
  getPlatformIcon,
  getPlatformDisplayName,
} from "../utils/platformIcons";
import { useTranslation } from "react-i18next";
import { useAppContext } from "@/context/AppContext";
import { AccountsSkeleton } from "@/components/Skeletons";

const ALL_PLATFORMS: Platform[] = [
  "facebook",
  "instagram",
  "linkedin",
  "tiktok",
  "youtube",
];

export const AccountsPage: React.FC = () => {
  const { t } = useTranslation();

  const {
    connectedPlatforms,
    connectingPlatforms,
    handleConnectPlatform,
    handleDisconnectPlatform,
    dispatch,
    checkConnectedPlatforms,
    linkedinPages,
    facebookPages,
    youtubeChannels,
    selectedLinkedinPage,
    selectedFacebookPage,
    selectedYoutubeChannel,
    setSelectedLinkedinPage,
    setSelectedFacebookPage,
    setSelectedYoutubeChannel,
    state,
    fetchAnalytics,
  }: any = useAppContext();

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);

  const renderPlatformIcon = (platform: Platform) => {
    const IconComponent = getPlatformIcon(platform);

    if (!IconComponent) {
      return (
        <span className="text-lg font-bold">{platform.substring(0, 2)}</span>
      );
    }

    return <IconComponent className="w-8 h-8" />;
  };

  const loadAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);

    try {
      await checkConnectedPlatforms();
    } catch (error) {
      console.error("Failed to check connected platforms:", error);
    } finally {
      setTimeout(() => {
        setIsLoadingAccounts(false);
      }, 300);
    }
  }, [checkConnectedPlatforms]);

  useEffect(() => {
    const handlePageShow = () => {
      dispatch({
        type: "SET_CONNECTING_PLATFORMS",
        payload: [],
      });

      loadAccounts();
    };

    loadAccounts();
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [dispatch, loadAccounts]);

  if (isLoadingAccounts) {
    return (
      <div className="max-w-full mx-auto md:px-4 px-3 py-2.5 flex flex-col gap-y-3">
        <div className="flex flex-col gap-y-3">
          <div>
            <h2 className="text-3xl font-bold theme-text-primary mb-1">
              {t("connect_accounts")}
            </h2>
          </div>
        </div>
        <AccountsSkeleton />
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto md:px-4 px-3 py-2.5 flex flex-col gap-y-3">
      <div className="flex flex-col gap-y-3">
        <div>
          <h2 className="text-3xl font-bold theme-text-primary mb-1">
            {t("connect_accounts")}
          </h2>
        </div>

        <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <span className="font-medium">
              {connectedPlatforms?.length ?? 0}
            </span>{" "}
            of <span className="font-medium">{ALL_PLATFORMS.length}</span>{" "}
            {t("platforms_connected")}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-y-3 mb-2">
        {ALL_PLATFORMS.map((platform) => {
          const isConnected = connectedPlatforms?.includes(platform) ?? false;
          const isConnecting = connectingPlatforms?.includes(platform);

          return (
            <div
              key={platform}
              className="flex items-center flex-row gap-2 justify-between p-4 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-md">
                  {renderPlatformIcon(platform)}
                </div>

                <div>
                  <h4 className="font-medium text-slate-900">
                    {getPlatformDisplayName(platform)}
                  </h4>
                  <p
                    className={`text-sm ${
                      isConnected ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {isConnected ? t("connected") : t("not_connected")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {isConnected ? (
                  <>
                    <button
                      onClick={() => handleConnectPlatform(platform)}
                      disabled={isConnecting}
                      className="p-2 text-gray-500 font-medium hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50 rounded-md hover:bg-gray-100"
                      title="Refresh connection"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDisconnectPlatform(platform)}
                      disabled={isConnecting}
                      className="p-2 text-gray-500 font-medium hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100"
                      title="Disconnect"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-2 px-3 py-1 rounded-full text-green-800 text-sm font-medium">
                      <svg
                        className="w-6 h-6"
                        fill="#4caf50"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnectPlatform(platform)}
                    disabled={isConnecting}
                    className="flex items-center gap-2 px-3 py-1 disabled:cursor-not-allowed capitalize border border-[#7650e3] rounded-md bg-purple-600 hover:bg-[#d7d7fc] hover:text-[#7650e3] text-sm font-medium text-white"
                  >
                    {isConnecting ? (
                      <>
                        <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                        <span>{t("connecting")}...</span>
                      </>
                    ) : (
                      t("connect")
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {connectedPlatforms?.includes("facebook") && facebookPages.length > 0 && (
        <div className="mb-2 p-3 bg-white border border-blue-600 rounded-md">
          <h4 className="font-medium text-black mb-2">
            {t("facebook_page_selection")}
          </h4>
          <p className="text-blue-700 text-sm mb-3">
            {t("choose_facebook_page_to_post_to")}
          </p>

          <select
            value={selectedFacebookPage}
            disabled={state?.analyticsLoading}
            onChange={(e) => {
              if (!state?.analyticsLoading) {
                setSelectedFacebookPage(e.target.value);
                localStorage.setItem("selectedFacebookPage", e.target.value);
                fetchAnalytics(true);
              }
            }}
            className="w-full p-2.5 border border-blue-600 rounded-md focus:ring-1 disabled:cursor-not-allowed focus:ring-blue-500"
          >
            {facebookPages.map((page: any) => (
              <option key={page.id} value={page.id}>
                {page.name} ({page.category})
              </option>
            ))}
          </select>
        </div>
      )}

      {connectedPlatforms?.includes("linkedin") && linkedinPages.length > 0 && (
        <div className="mb-2 p-3 bg-white border border-blue-600 rounded-md">
          <h4 className="font-medium text-black mb-2">
            LinkedIn Page Selection
          </h4>
          <p className="text-blue-700 text-sm mb-3">
            Choose your default LinkedIn page for publishing:
          </p>

          <select
            value={selectedLinkedinPage}
            disabled={state?.analyticsLoading}
            onChange={(e) => {
              if (!state?.analyticsLoading) {
                setSelectedLinkedinPage(e.target.value);
                localStorage.setItem("selectedlinkedinPage", e.target.value);
                fetchAnalytics(true);
              }
            }}
            className="w-full p-2.5 border border-blue-600 rounded-md focus:ring-1 disabled:cursor-not-allowed focus:ring-blue-500"
          >
            <option value="">Personal Account</option>
            {linkedinPages.map((page: any) => (
              <option key={page.urn} value={page.urn}>
                {page.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {connectedPlatforms?.includes("youtube") &&
        youtubeChannels.length > 0 && (
          <div className="mb-2 p-4 bg-white border border-blue-600 rounded-md">
            <h4 className="font-medium text-black mb-2">
              {t("youtube_channel_selection")}
            </h4>
            <p className="text-blue-700 text-sm mb-3">
              {t("choose_youtube_channel_to_upload_to")}
            </p>

            <select
              value={selectedYoutubeChannel}
              disabled={state?.analyticsLoading}
              onChange={(e) => {
                if (!state?.analyticsLoading) {
                  setSelectedYoutubeChannel(e.target.value);
                  localStorage.setItem(
                    "selectedYoutubeChannel",
                    e.target.value
                  );
                  fetchAnalytics(true);
                }
              }}
              className="w-full p-2.5 border border-blue-600 rounded-md focus:ring-1 disabled:cursor-not-allowed focus:ring-blue-500"
            >
              {youtubeChannels.map((channel: any) => (
                <option key={channel.id} value={channel.id}>
                  {channel.snippet.title}
                </option>
              ))}
            </select>
          </div>
        )}
    </div>
  );
};
